import { useEffect, useState, RefObject } from 'react';
import { pageZoom } from './landingTokens';

/** Shared helpers for the pinned, scroll-driven sections (the gap). */

export const HEADER_HEIGHT = 72;

export const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Progress 0..1 of scrolling through a tall section whose content sticks below
 * the header. Returns 1 when disabled, so callers render the finished state.
 */
export const useScrollProgress = (ref: RefObject<HTMLElement | null>, enabled: boolean) => {
  const [progress, setProgress] = useState(enabled ? 0 : 1);

  useEffect(() => {
    if (!enabled) {
      setProgress(1);
      return undefined;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      // The rect is in screen pixels, where the zoomed header is taller.
      const header = HEADER_HEIGHT * pageZoom(window.innerWidth);
      const rect = el.getBoundingClientRect();
      const travel = rect.height - (window.innerHeight - header);
      setProgress(travel > 0 ? clamp01((header - rect.top) / travel) : 1);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ref, enabled]);

  return progress;
};
